/**
 * EntityRateAssignmentSection - Reusable rate assignment section for entity edit drawers
 *
 * Features:
 * - Displays current effective rate with source attribution
 * - Shows rate assignment timeline (current, upcoming, past)
 * - Add/edit/delete rate assignments
 * - Works for companies, groups, sites, and units
 */

import { useState, useCallback } from 'react';
import {
  Zap,
  Plus,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RateAssignmentDrawer } from '@/components/settings/RateAssignmentDrawer';
import { RateAssignmentTimeline } from '@/components/settings/RateAssignmentTimeline';
import { EffectiveRateDisplay } from '@/components/settings/EffectiveRateDisplay';
import { useRateAssignments } from '@/hooks/useRateAssignments';
import { useEffectiveRate, useGroupEffectiveRate, GroupEffectiveRateResponse } from '@/hooks/useEffectiveRate';
import { useEnergyRates } from '@/hooks/useEnergyRates';
import type { RateAssignmentWithDetails, EffectiveRateResponse, RateSource } from '@/types/rates';
import { cn } from '@/lib/utils';

export type RateEntityType = 'company' | 'group' | 'site' | 'unit';

interface EntityRateAssignmentSectionProps {
  /** The type of entity */
  entityType: RateEntityType;
  /** The entity ID */
  entityId: string;
  /** The entity name (for display) */
  entityName: string;
  /** Optional callback when assignments change */
  onAssignmentChange?: () => void;
  /** Whether the section is initially expanded */
  defaultExpanded?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// Transform group effective rate to match EffectiveRateResponse format
function transformGroupRateToEffectiveRate(
  rate: GroupEffectiveRateResponse | null
): EffectiveRateResponse | null {
  if (!rate) return null;

  return {
    rateId: rate.rateId || null,
    rateName: rate.rateName,
    gridImportRate: rate.gridImportRate,
    gridSellRate: rate.gridSellRate,
    dieselCostPerGallon: rate.dieselCostPerGallon ?? 0,
    utilityProvider: rate.utilityProvider ?? undefined,
    nemVersion: rate.nemVersion ?? undefined,
    rateSource: rate.rateSource as RateSource,
    sourceName: rate.sourceName || 'Unknown',
    effectiveFrom: undefined,
    effectiveTo: undefined,
  };
}

export function EntityRateAssignmentSection({
  entityType,
  entityId,
  entityName,
  onAssignmentChange,
  defaultExpanded = true,
  className,
}: EntityRateAssignmentSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<RateAssignmentWithDetails | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<RateAssignmentWithDetails | null>(null);

  // Fetch assignments for this entity
  const {
    assignments,
    isLoading: assignmentsLoading,
    isSubmitting,
    fetchAssignments: _fetchAssignments,
    deleteAssignment,
    refresh: refreshAssignments,
  } = useRateAssignments({
    entityType: entityType === 'site' ? 'group' : entityType,
    entityId,
    includeExpired: true,
    autoFetch: true,
  });

  // Fetch available rates for the dropdown
  const { rates, isLoading: ratesLoading } = useEnergyRates({ autoFetch: true });

  // Fetch effective rate - use different hook based on entity type
  const {
    rate: unitEffectiveRate,
    isLoading: unitRateLoading,
    error: unitRateError,
    refetch: refetchUnitRate,
  } = useEffectiveRate({
    unitId: entityType === 'unit' ? entityId : undefined,
    autoFetch: entityType === 'unit',
  });

  const {
    rate: groupEffectiveRate,
    isLoading: groupRateLoading,
    error: groupRateError,
    refetch: refetchGroupRate,
  } = useGroupEffectiveRate({
    groupId: entityType === 'group' || entityType === 'site' ? entityId : undefined,
    autoFetch: entityType === 'group' || entityType === 'site',
  });

  // Determine which rate and loading state to use
  const effectiveRate = entityType === 'unit'
    ? unitEffectiveRate
    : entityType === 'company'
    ? null // Companies don't have a single "effective rate" - they're the source
    : transformGroupRateToEffectiveRate(groupEffectiveRate);

  const effectiveRateLoading = entityType === 'unit'
    ? unitRateLoading
    : groupRateLoading;

  const effectiveRateError = entityType === 'unit'
    ? unitRateError
    : groupRateError;

  const _refetchEffectiveRate = entityType === 'unit'
    ? refetchUnitRate
    : refetchGroupRate;

  // Handle opening drawer for new assignment
  const handleAddClick = useCallback(() => {
    setEditingAssignment(null);
    setDrawerOpen(true);
  }, []);

  // Handle edit click
  const handleEditClick = useCallback((assignment: RateAssignmentWithDetails) => {
    setEditingAssignment(assignment);
    setDrawerOpen(true);
  }, []);

  // Handle delete click
  const handleDeleteClick = useCallback((assignment: RateAssignmentWithDetails) => {
    setAssignmentToDelete(assignment);
    setDeleteDialogOpen(true);
  }, []);

  // Handle drawer close
  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setEditingAssignment(null);
  }, []);

  // Handle drawer submit (after successful create/update)
  const handleDrawerSubmit = useCallback(async () => {
    setDrawerOpen(false);
    setEditingAssignment(null);
    await refreshAssignments();
    if (entityType === 'unit') {
      await refetchUnitRate();
    } else if (entityType === 'group' || entityType === 'site') {
      await refetchGroupRate();
    }
    onAssignmentChange?.();
  }, [refreshAssignments, entityType, refetchUnitRate, refetchGroupRate, onAssignmentChange]);

  // Handle delete confirm
  const handleDeleteConfirm = useCallback(async () => {
    if (!assignmentToDelete) return;

    const success = await deleteAssignment(assignmentToDelete.id);
    if (success) {
      setDeleteDialogOpen(false);
      setAssignmentToDelete(null);
      await refreshAssignments();
      if (entityType === 'unit') {
        await refetchUnitRate();
      } else if (entityType === 'group' || entityType === 'site') {
        await refetchGroupRate();
      }
      onAssignmentChange?.();
    }
  }, [assignmentToDelete, deleteAssignment, refreshAssignments, entityType, refetchUnitRate, refetchGroupRate, onAssignmentChange]);

  // Refresh all data
  const handleRefresh = useCallback(async () => {
    await refreshAssignments();
    if (entityType === 'unit') {
      await refetchUnitRate();
    } else if (entityType === 'group' || entityType === 'site') {
      await refetchGroupRate();
    }
  }, [refreshAssignments, entityType, refetchUnitRate, refetchGroupRate]);

  const isLoading = assignmentsLoading || ratesLoading;

  return (
    <div className={cn('border border-border rounded-lg', className)}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-[#8FB560]" />
              <span className="font-medium">Rate Assignments</span>
              {assignments.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {assignments.length}
                </Badge>
              )}
            </div>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-4">
            {/* Effective Rate Display (for units and groups/sites) */}
            {entityType !== 'company' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Current Effective Rate
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={effectiveRateLoading || assignmentsLoading}
                    className="h-7"
                  >
                    <RefreshCw className={cn(
                      "h-3.5 w-3.5",
                      (effectiveRateLoading || assignmentsLoading) && "animate-spin"
                    )} />
                  </Button>
                </div>
                <EffectiveRateDisplay
                  rate={effectiveRate}
                  isLoading={effectiveRateLoading}
                  error={effectiveRateError}
                  variant="detailed"
                />
              </div>
            )}

            {/* For companies, show a simpler message */}
            {entityType === 'company' && (
              <div className="text-sm text-muted-foreground">
                Rates assigned to this company will apply to all groups and units
                that don&apos;t have their own rate assignment.
              </div>
            )}

            {/* Actions Bar */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {assignments.length === 0
                  ? 'No rate assignments'
                  : `${assignments.length} assignment${assignments.length !== 1 ? 's' : ''}`}
              </span>
              <Button
                size="sm"
                onClick={handleAddClick}
                className="bg-[#8FB560] hover:bg-[#7a9d4e]"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Rate
              </Button>
            </div>

            {/* Timeline */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : assignments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <AlertCircle className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  No rate assignments for this {entityType === 'site' ? 'site' : entityType}.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {entityType === 'company'
                    ? 'Add a rate to set the default for all groups and units.'
                    : entityType === 'unit'
                    ? 'The rate will be inherited from the parent group or company.'
                    : 'The rate will be inherited from the parent company.'}
                </p>
              </div>
            ) : (
              <RateAssignmentTimeline
                assignments={assignments}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                showActions={true}
              />
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Rate Assignment Drawer */}
      <RateAssignmentDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        onSubmit={handleDrawerSubmit}
        assignment={editingAssignment || undefined}
        entityType={entityType === 'site' ? 'group' : entityType}
        entityId={entityId}
        entityName={entityName}
        rates={rates}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Rate Assignment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this rate assignment?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
